import * as _ from 'lodash';
import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';

import { selectOptionFactory } from '../../utils';
import Button from '../generic/Button/Button';
import { BUTTON_TYPE } from '../generic/Button/button-constants';
import ButtonGroup from '../generic/Button/ButtonGroup';
import Select from '../generic/Select/Select';
import type { Notification } from '../../../shared/model-typings';
import type { MinimalUser } from '../../../shared/user';
import type { SelectOption } from '../../types';

type ModalEditNotificationsProps = {
  editingNotification?: Pick<Notification, 'subscribed_users'>;
  users: MinimalUser[];
  onConfirm(subscribedUserIds: number[]): void;
  onHide(): void;
};

/**
 * Allows the user to select which users will receive an email when Fossa finds new issues.
 */
const ModalEditNotifications = (props: ModalEditNotificationsProps) => {
  const [subscribedUserIds, setSubscribedUserIds] = useState<number[]>([]);

  useEffect(() => {
    if (props.editingNotification) {
      setSubscribedUserIds(_.intersectionBy<number, MinimalUser>(
        props.editingNotification.subscribed_users || [],
        props.users,
        userOrId => _.isNumber(userOrId) ? userOrId : userOrId.id,
      ));
    }
  }, [props.editingNotification, props.users]);

  const onConfirm = () => {
    props.onConfirm(subscribedUserIds);
    props.onHide();
  };

  const { editingNotification } = props;
  if (!editingNotification) {
    return null;
  }

  const userOptions: Array<SelectOption<number>> = props.users.map(user =>
    selectOptionFactory(user.id, user.username || user.email as string));

  const subscribedUserOptions = _.intersectionBy<SelectOption<number>, number>(
    userOptions,
    subscribedUserIds,
    optionOrId => _.isNumber(optionOrId) ? optionOrId : optionOrId.value,
  );

  return (
    <Modal onHide={props.onHide} show animation>
      <Modal.Header closeButton>
        <Modal.Title>Subscribed users</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>Select which users will receive an email when FOSSA finds new issues.</p>
        <Select
          isMulti
          name="subscribed-users"
          value={subscribedUserOptions}
          options={userOptions}
          onChange={(values) => {
            setSubscribedUserIds(values.map(opt => opt.value),
            );
          }}
        />
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button
            type={BUTTON_TYPE.SECONDARY}
            size="medium"
            onClick={props.onHide}
          >
            Cancel
          </Button>
          <Button
            type={BUTTON_TYPE.PRIMARY}
            size="medium"
            onClick={onConfirm}
          >
            OK
          </Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalEditNotifications;
